import { isClientError } from '@utils/graphqlUtils';
import { z } from 'zod/v4';

export enum UnapplicableFilterType {
  Abac = 'Abac',
  Global = 'Global',
}

export const unapplicableFilterExtension = z.object({
  filter_type: z.enum(UnapplicableFilterType),
  unreachable_fields: z.string().optional().nullable(),
  root_projection: z.string().optional().nullable(),
});

export type UnapplicableFilterDetails = z.infer<typeof unapplicableFilterExtension>;

export type UnapplicableFilterState =
  { type: UnapplicableFilterType.Global; message: string; fieldNames: string[] } | { type: UnapplicableFilterType.Abac; message: string };

export interface GqlErrorLike {
  extensions?: {
    unapplicableFilter?: unknown;
  };
}

export const UNAPPLICABLE_FILTER_TITLE = 'No Data Found';

export const UNAPPLICABLE_FILTER_MESSAGES: Record<UnapplicableFilterType, string> = {
  [UnapplicableFilterType.Global]: "The following filters can't be applied to this widget",
  [UnapplicableFilterType.Abac]: "Your content permissions filters can't be applied to this widget",
};

const flattenToGqlErrors = (errors: unknown[]): GqlErrorLike[] => {
  const flattenedErrors: GqlErrorLike[] = [];
  const pendingErrors: unknown[] = [...errors];
  const visitedErrors = new Set<unknown>();

  while (pendingErrors.length) {
    const error = pendingErrors.shift();
    if (!visitedErrors.has(error)) {
      visitedErrors.add(error);

      if (Array.isArray(error)) {
        pendingErrors.push(...error);
      } else if (isClientError(error)) {
        pendingErrors.push(...(error.response?.errors || []));
      } else {
        flattenedErrors.push(error as GqlErrorLike);
      }
    }
  }

  return flattenedErrors;
};

export const getUnapplicableFilterError = (errors: unknown[] | undefined | null): UnapplicableFilterDetails | undefined => {
  const parsedResults = flattenToGqlErrors(errors || []).map(error =>
    unapplicableFilterExtension.safeParse(error?.extensions?.unapplicableFilter)
  );
  return parsedResults.find(parsed => parsed.success)?.data;
};

export const isUnapplicableFilterError = (error: unknown): boolean => Boolean(getUnapplicableFilterError([error]));

const getUnapplicableFilterFieldsSummary = (
  errors: unknown[] | undefined | null,
  filterType: UnapplicableFilterType,
  getFieldDisplayName: (fieldName: string) => string = fieldName => fieldName
): string | undefined => {
  const error = getUnapplicableFilterError(errors);
  if (error?.filter_type !== filterType || !error.unreachable_fields) {
    return undefined;
  }

  const fields = [
    ...new Set(
      error.unreachable_fields
        .split(',')
        .map(field => field.trim())
        .filter(Boolean)
    ),
  ];
  if (!fields.length) {
    return undefined;
  }

  const displayedFields = fields.slice(0, 2).map(getFieldDisplayName).join(', ');
  const remainingFieldsCount = fields.length - 2;
  return `${displayedFields}${remainingFieldsCount > 0 ? ` and ${remainingFieldsCount} more` : ''}`;
};

export const getUnapplicableGlobalFilterFieldsSummary = (
  errors: unknown[] | undefined | null,
  getFieldDisplayName?: (fieldName: string) => string
): string | undefined => getUnapplicableFilterFieldsSummary(errors, UnapplicableFilterType.Global, getFieldDisplayName);

export const getUnapplicableFieldNames = (errors: unknown[] | undefined | null): string[] => {
  const error = getUnapplicableFilterError(errors);
  if (error?.filter_type !== UnapplicableFilterType.Global || !error.unreachable_fields) {
    return [];
  }
  return [
    ...new Set(
      error.unreachable_fields
        .split(',')
        .map(field => field.trim())
        .filter(Boolean)
    ),
  ];
};

export const getUnapplicableFilterKeys = (
  fieldNames: string[],
  filterOptions: { value: string; otherAliases?: string[] }[]
): Set<string> => {
  const fieldNameSet = new Set(fieldNames);
  const matchedValues = filterOptions
    .filter(({ value, otherAliases }) => fieldNameSet.has(value) || otherAliases?.some(alias => fieldNameSet.has(alias)))
    .map(({ value }) => value);
  return new Set([...fieldNames, ...matchedValues]);
};

export const getUnapplicableAbacFilterMessage = (
  errors: unknown[] | undefined | null,
  getFieldDisplayName?: (fieldName: string) => string
): string | undefined => {
  const error = getUnapplicableFilterError(errors);
  if (error?.filter_type !== UnapplicableFilterType.Abac) {
    return undefined;
  }
  const fieldsSummary = getUnapplicableFilterFieldsSummary(errors, UnapplicableFilterType.Abac, getFieldDisplayName);
  return `${UNAPPLICABLE_FILTER_MESSAGES[UnapplicableFilterType.Abac]}${fieldsSummary ? `: ${fieldsSummary}` : ''}. Please reach out to your account admin for resolution.`;
};

export const getUnapplicableFilterState = (
  errors: unknown[] | undefined | null,
  getFieldDisplayName?: (fieldName: string) => string
): UnapplicableFilterState | undefined => {
  const error = getUnapplicableFilterError(errors);
  if (!error) {
    return undefined;
  }
  if (error.filter_type === UnapplicableFilterType.Abac) {
    const message = getUnapplicableAbacFilterMessage(errors, getFieldDisplayName);
    return message ? { type: UnapplicableFilterType.Abac, message } : undefined;
  }
  const fieldsSummary = getUnapplicableGlobalFilterFieldsSummary(errors, getFieldDisplayName);
  return {
    type: UnapplicableFilterType.Global,
    message: `${UNAPPLICABLE_FILTER_MESSAGES[UnapplicableFilterType.Global]}${fieldsSummary ? `: ${fieldsSummary}` : ''}.`,
    fieldNames: getUnapplicableFieldNames(errors),
  };
};

export const getUnapplicableGlobalFilterOptionsMessage = (
  errors: unknown[] | undefined | null,
  getFieldDisplayName?: (fieldName: string) => string
): string | undefined => {
  const fieldsSummary = getUnapplicableGlobalFilterFieldsSummary(errors, getFieldDisplayName);
  return fieldsSummary ? `The following filters can't be applied to this filter: ${fieldsSummary}.` : undefined;
};
